// frontend/src/App.tsx
import React, { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/i18n';
import { ToastContainer } from 'react-toastify';

// Componentes inmediatos
import Login from './features/authentication/pages/Login/Login';
import NavBar from './shared/components/layout/NavBar';
import ProtectedRoute from './shared/components/layout/ProtectedRoute';
import { NavBarProvider } from './NavBarContext';
import { UserProvider, useUserArea } from './contexts/UserContext'; // ✅ Usar contexto

// Hooks personalizados
import { useAuth } from './App/hooks/useAuth';
import useI18n from './shared/hooks/i18n/useI18n';

// Utilidades
import { getAreaBasedRedirect, getAuthBasedRedirect } from './App/utils/redirectHelpers';

// Lazy components
import * as LazyComponents from './App/lazyComponents';

// Tipos
import { Area } from './types/common.types';

// Estilos
import './App.css';
import 'react-toastify/dist/ReactToastify.css';

// Áreas permitidas constantes
const ALL_AREAS: Area[] = ['ESG', 'MedioAmbiental', 'EstandaresSociales', 'GobiernoCorporativo', 'Financiero'];

/**
 * Componente interno que usa los hooks del contexto
 * Debe estar dentro del UserProvider
 */
function AppContent(): React.ReactElement {
	const { isLoggedIn, setIsLoggedIn, authChecked, handleLogout } = useAuth();

	const { area } = useUserArea();

	const { t } = useI18n();

	if (!authChecked) {
		return <p>{t('common.verifyAuth')}</p>;
	}

	return (
		<NavBarProvider>
						<div className='App'>
						{isLoggedIn && <NavBar onLogout={handleLogout} />}
						<main>
							<Suspense
								fallback={
									<div
										style={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											height: '100vh',
											fontSize: '1.2rem',
											color: '#666',
										}}>
										{t('common.loading', 'Cargando...')}
									</div>
								}>
								<Routes>
									{/* Ruta raíz */}
									<Route
										path='/'
										element={
											isLoggedIn ? (
												getAreaBasedRedirect(area)
											) : (
												<Login onLogin={() => setIsLoggedIn(true)} />
											)
										}
									/>

									{/* Reset Password */}
									<Route path='/reset-password' element={<LazyComponents.ResetPassword />} />

									{/* SuperAdmin Routes */}
									<Route
										path='/superadmin/dashboard'
										element={
											<ProtectedRoute
												element={<LazyComponents.SuperAdminDashboard />}
												allowedAreas={['SuperAdmin']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/superadmin/create-system-user'
										element={
											<ProtectedRoute
												element={<LazyComponents.CreateSystemUser />}
												allowedAreas={['SuperAdmin']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>

									{/* System Routes */}
									<Route
										path='/system-dashboard'
										element={
											<ProtectedRoute
												element={<LazyComponents.DashboardSystem />}
												allowedAreas={['System']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/company-info'
										element={
											<ProtectedRoute
												element={<LazyComponents.CompanyInfo />}
												allowedAreas={['System']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/create-user'
										element={
											<ProtectedRoute
												element={<LazyComponents.CreateUser />}
												allowedAreas={['System']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/update-user'
										element={
											<ProtectedRoute
												element={<LazyComponents.UpdateUser />}
												allowedAreas={['System']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/delete-user'
										element={
											<ProtectedRoute
												element={<LazyComponents.DeleteUser />}
												allowedAreas={['System']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/audit-trail'
										element={
											<ProtectedRoute
												element={<LazyComponents.AuditTrail />}
												allowedAreas={['System']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>

									{/* Responsables Routes */}
									<Route
										path='/casos'
										element={
											<ProtectedRoute
												element={<LazyComponents.Casos />}
												allowedAreas={ALL_AREAS}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/process/:FormularioId/forms/:casoId'
										element={
											<ProtectedRoute
												element={<LazyComponents.VistaFormulario />}
												allowedAreas={ALL_AREAS}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/dashboard/forms/:casoId/answer/:respuestaId'
										element={
											<ProtectedRoute
												element={<LazyComponents.VistaFormulario />}
												allowedAreas={ALL_AREAS}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/process/:casoId/form/:procesoId/question/:preguntaId'
										element={
											<ProtectedRoute
												element={<LazyComponents.VistaPregunta />}
												allowedAreas={ALL_AREAS}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>

									{/* Sostenibilidad Routes */}
									<Route
										path='/procesos'
										element={
											<ProtectedRoute
												element={<LazyComponents.ListadeProcesos />}
												allowedAreas={['ESG']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>
									<Route
										path='/casos/:casoId/procesos/:procesoId/vista-proceso-acabado'
										element={
											<ProtectedRoute
												element={<LazyComponents.VistaProcesoAcabado />}
												allowedAreas={['MedioAmbiental', 'ESG', 'EstandaresSociales', 'GobiernoCorporativo']}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>

									{/* Extracción Route */}
									<Route
										path='/extraccion'
										element={
											<ProtectedRoute
												element={<LazyComponents.ExtraccionDashboard />}
												allowedAreas={['Extraccion' as Area]}
												authChecked={authChecked}
												isLoggedIn={isLoggedIn}
											/>
										}
									/>

									{/* Catch-all Route */}
									<Route path='*' element={getAuthBasedRedirect(isLoggedIn, area)} />
								</Routes>
							</Suspense>
						</main>
						<footer className='app-footer'>
							{t('footer')}
						</footer>
					</div>
				</NavBarProvider>
	);
}

/**
 * Componente principal App
 * Envuelve la aplicación con todos los providers necesarios
 */
function App(): React.ReactElement {
	return (
		<I18nextProvider i18n={i18n}>
			<ToastContainer
				position="top-right"
				autoClose={3000}
				hideProgressBar={false}
				newestOnTop
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="light"
			/>
			<Router>
				<UserProvider>
					<AppContent />
				</UserProvider>
			</Router>
		</I18nextProvider>
	);
}

export default App;
